import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import TripCarouselCards from './TripCarouselCards'
import FullWidthCarousel from 'LuxKit/Carousel/FullWidthCarousel'
import { useExploreItems } from 'tripPlanner/hooks/api/explore'
import calculateDistance from 'lib/geo/distanceBetweenPoints'
import TripCarouselCardsLoading from './TripCarouselCardsLoading'
import { rem } from 'polished'
import styled from 'styled-components'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import Sheet from 'components/utils/Sheet'
import TextButton from 'LuxKit/Button/TextButton'
import type { ExploreCategory } from 'tripPlanner/pages/trip/Explore/common'

interface Props {
  type: ExploreCategory['label']
  className?: string
  lat: number | undefined
  lng: number | undefined
  location: string | undefined
  tripId: string | undefined
  showDistance: boolean
}

const TransparentCard = styled.div`
  height: ${rem(115)};
  width: ${rem(312)};
  background: rgba(255,255,255, 0.16);
`

function TripCarousel({ type, className, lat, lng, location, tripId, showDistance }: Props) {
  const intl = useIntl()
  const { data: items, isLoading, isFetching, isRefetching, isError } = useExploreItems({
    query: `${type} in ${location}`,
    locationBias: lat !== undefined && lng !== undefined ? [lat, lng] : undefined,
  })

  const itemsWithDistance = useMemo(() => {
    if (!items) return []
    return items.map((item) => ({
      ...item,
      distance: showDistance && lat !== undefined && lng !== undefined
        ? calculateDistance(lat, lng, item.place.point.lat, item.place.point.lng)
        : undefined,
    }))
  }, [items, lat, lng, showDistance])

  const showLoading = isLoading || isFetching || isRefetching || (!items && !isError)
  const showContent = !showLoading && itemsWithDistance.length > 0

  return (
    <FullWidthCarousel
      className={className}
      snap="start"
      gap={16}
    >
      {showLoading && (
        <>
          <TripCarouselCardsLoading />
          <TripCarouselCardsLoading />
          <TripCarouselCardsLoading />
          <TripCarouselCardsLoading />
          <TripCarouselCardsLoading />
        </>
      )}
      {showContent && itemsWithDistance.slice(0, 5).map((item) => (
        <TripCarouselCards
          key={item.id}
          item={item}
          distance={item.distance}
          showDistance={showDistance}
          tripId={tripId}
        />
      ))}
      <TransparentCard>
        <Sheet padding={16}>
          <Group direction="vertical" gap={12} verticalAlign="space-between">
            <Group direction="vertical" gap={4} horizontalAlign="center" verticalAlign="center">
              <BodyText variant="small" weight="bold">
                {intl.formatMessage({
                  id: 'N6FO55Vu4KKBGK4rbr9p',
                  defaultMessage: 'Add more to your trip',
                  description: 'Heading encouraging user to add more items to their trip',
                })}
              </BodyText>
              <BodyText variant="small">
                {intl.formatMessage({
                  id: 'mpMOpY5DfuIs6BOMEjWd',
                  defaultMessage: 'Your escape, your way - dining, sights & more.',
                  description: 'Subtitle encouraging trip customization in the trip planner feature',
                })}
              </BodyText>
            </Group>
            <Group direction="horizontal" horizontalAlign="center" verticalAlign="center">
              {tripId && <TextButton kind="secondary" size="small" to={`/trip-planner/trip/${tripId}`}>
                {intl.formatMessage({
                  id: '-ea0yLsg7Rlsc5cKrh_F',
                  defaultMessage: 'View trip plan',
                  description: 'Button label to view the trip plan in the trip planner feature',
                })}
              </TextButton>}
              {!tripId && <TextButton kind="secondary" size="small" to={`/trip-planner/generate-trip?location=${location}`}>
                {intl.formatMessage({
                  id: 'fVdHuhq-YOpyUE5rXxUA',
                  defaultMessage: 'Start planning - It’s free',
                  description: 'Button label to start planning a trip in the trip planner feature',
                })}
              </TextButton>}
            </Group>
          </Group>
        </Sheet>
      </TransparentCard>
    </FullWidthCarousel>
  )
}

export default TripCarousel
